import { provideZoneChangeDetection } from "@angular/core";
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
import { MSAL_INSTANCE } from '@azure/msal-angular';
import { PublicClientApplication } from '@azure/msal-browser';

import { AppModule } from './app/app.module';
import { msalConfig } from './app/auth-config';
import { diag, diagMsal, installLoopDiagnostics } from './app/loop-diagnostics';

/**
 * MSAL is initialised exactly once, before Angular starts.
 *
 * msal-browser requires initialize() to resolve before any other API is used, and it
 * is not safe to run concurrently: the flag that makes later calls short-circuit is
 * only set after several awaits. msal-angular initialises lazily from the guard, the
 * redirect component and the interceptor, so a fresh browser session had three
 * initialisations racing. Each set up the encrypted localStorage cache with its own
 * key, and the losers then deleted the account the winner had just stored — which is
 * what sent people back to Microsoft every eleven seconds. Handing Angular an
 * already-initialised instance means every one of those calls returns immediately.
 */
async function bootstrap(): Promise<void> {
  installLoopDiagnostics();

  const msalInstance = new PublicClientApplication(msalConfig);

  // Recorded here because MSAL's own initializeStart line is written per event
  // listener, and no listener exists this early: without this the one initialise
  // that now happens would be invisible in the buffer.
  const started = performance.now();
  diag('msal:init', { phase: 'start' });
  await msalInstance.initialize();
  diagMsal('msal:init:done', msalInstance, { ms: Math.round(performance.now() - started) });

  await platformBrowserDynamic([{ provide: MSAL_INSTANCE, useValue: msalInstance }])
    .bootstrapModule(AppModule, { applicationProviders: [provideZoneChangeDetection()] });
}

bootstrap().catch(err => console.error(err));
